// src/services/boxaiVaultService.js
import {
    getProvider,
    ensureBsc,
    requestAccounts,
    add0x,
    strip0x,
    addrParam,
    u256Param,
    pad32,
    parseUnits,
    formatUnits,
    ethCall,
    ethEstimateGas,
    ethGasPrice,
    sendTransaction,
    waitReceipt,
  } from "@/utils/eth";
  
  // ========== Constants ==========
  export const VAULT = "0xBCBf42df97335d97a2e2b9AD67CD897CA796b134";
  
  // USDT (BSC) - 你这里固定 18 decimals
  export const USDT = "0x55d398326f99059fF775485246999027B3197955";
  const USDT_DECIMALS = 18;
  
  // ========== Function Selectors (keccak256 first 4 bytes) ==========
  const SEL = {
    // ERC20
    balanceOf: "0x70a08231",
    allowance: "0xdd62ed3e",
    approve: "0x095ea7b3",
  
    // Vault
    deposit: "0x045434c4", // deposit(uint256,uint256,bytes32)
  };
  
  // ========== Event Topics (keccak256 full 32 bytes) ==========
  const TOPIC = {
    DepositCreated:
      "0x6cd492487e784148a23bef5344a26a3f8521d76e5bed9a38ca8fd5187a757af8",
    VTokenForwarded:
      "0xf54ee6412d283ef8ba418ecd5936f7a690b767603242180811724c25dbadd1e5",
  };
  
  // ========== Helpers ==========
  function bytes32Param(hex0x) {
    const h = strip0x(String(hex0x || ""));
    if (h.length !== 64) throw new Error("clientOrderKey 必须是 32 bytes (64 hex)");
    return h.toLowerCase();
  }
  
  function hexToBigInt(hex) {
    return BigInt(add0x(hex));
  }
  
  function readU256FromData(data0x, wordIndex) {
    const h = strip0x(data0x);
    const start = wordIndex * 64;
    const word = h.slice(start, start + 64);
    return BigInt("0x" + word);
  }
  
  function readBytes32FromData(data0x, wordIndex) {
    const h = strip0x(data0x);
    const start = wordIndex * 64;
    const word = h.slice(start, start + 64);
    return "0x" + word;
  }
  
  function topicToAddress(topic32) {
    // topic 是 32 bytes，地址在低 20 bytes
    const h = strip0x(topic32);
    return add0x(h.slice(24)).toLowerCase();
  }
  
  export function randomClientOrderKey() {
    // 生成随机 32 bytes，避免 usedClientOrderKey 冲突
    const buf = new Uint8Array(32);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    let hex = "";
    buf.forEach((b) => (hex += b.toString(16).padStart(2, "0")));
    return "0x" + hex;
  }
  
  // ========== Read USDT ==========
  export async function getUsdtBalanceFormatted(owner) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const data = add0x(SEL.balanceOf + addrParam(owner));
    const hex = await ethCall({ to: USDT, data }, "latest", provider);
    const bal = hexToBigInt(hex);
    return formatUnits(bal, USDT_DECIMALS, 2);
  }
  
  export async function getUsdtAllowance(owner) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    // allowance(owner, spender)
    const data = add0x(
      SEL.allowance + addrParam(owner) + addrParam(VAULT)
    );
    const hex = await ethCall({ to: USDT, data }, "latest", provider);
    return hexToBigInt(hex);
  }
  
  // ========== Approve ==========
  export async function approveUsdt(amountHuman, { onTxHash } = {}) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const amountWei = parseUnits(String(amountHuman), USDT_DECIMALS);
  
    // approve(spender, amount)
    const data = add0x(
      SEL.approve + addrParam(VAULT) + u256Param(amountWei)
    );
  
    const txBase = { from, to: USDT, data };
    const gas = await ethEstimateGas(txBase, provider);
    const gasPrice = await ethGasPrice(provider);
  
    const { hash } = await sendTransaction({ ...txBase, gas, gasPrice }, provider);
    if (onTxHash) onTxHash(hash);
  
    const receipt = await waitReceipt(hash, {}, provider);
    return { hash, receipt };
  }
  
  // ========== Deposit (Vault) ==========
  export async function depositToVault(planId, amountHuman, clientOrderKey, { onTxHash } = {}) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const amountWei = parseUnits(String(amountHuman), USDT_DECIMALS);
  
    // deposit(uint256 planId, uint256 amount, bytes32 clientOrderKey)
    const data = add0x(
      SEL.deposit +
        u256Param(BigInt(planId)) +
        u256Param(amountWei) +
        bytes32Param(clientOrderKey)
    );
  
    const txBase = { from, to: VAULT, data };
    const gas = await ethEstimateGas(txBase, provider);
    const gasPrice = await ethGasPrice(provider);
  
    const { hash } = await sendTransaction({ ...txBase, gas, gasPrice }, provider);
    if (onTxHash) onTxHash(hash);
  
    const receipt = await waitReceipt(hash, {}, provider);
    return { hash, receipt };
  }
  
  // ========== One-shot placeOrder: allowance -> approve -> deposit ==========
  export async function placeOrder({
    planId,
    amount, // human amount, e.g. "100"
    clientOrderKey, // optional
    onStep, // (stepName, payload) => void
  } = {}) {
    const provider = getProvider();
    await ensureBsc(provider);
  
    const from = await requestAccounts(provider);
    const amtWei = parseUnits(String(amount), USDT_DECIMALS);
    const key = clientOrderKey || randomClientOrderKey();
  
    onStep && onStep("CHECK_ALLOWANCE", { from, amount, planId, clientOrderKey: key });
  
    const allowance = await getUsdtAllowance(from);
    if (allowance < amtWei) {
      onStep && onStep("APPROVE_REQUIRED", { allowance: allowance.toString() });
  
      const { hash: approveHash, receipt: approveRcpt } = await approveUsdt(amount, {
        onTxHash: (h) => onStep && onStep("APPROVE_TX", { hash: h }),
      });
  
      onStep && onStep("APPROVE_CONFIRMED", { hash: approveHash, status: approveRcpt?.status });
    } else {
      onStep && onStep("APPROVE_SKIPPED", { allowance: allowance.toString() });
    }
  
    onStep && onStep("DEPOSIT_SENDING", { planId, amount, clientOrderKey: key });
  
    const { hash: depositHash, receipt: depositRcpt } = await depositToVault(planId, amount, key, {
      onTxHash: (h) => onStep && onStep("DEPOSIT_TX", { hash: h }),
    });
  
    const parsed = parseDepositCreatedFromReceipt(depositRcpt);
  
    onStep && onStep("DEPOSIT_CONFIRMED", {
      hash: depositHash,
      status: depositRcpt?.status,
      parsed,
    });
  
    return { from, approveNeeded: allowance < amtWei, depositHash, depositRcpt, parsed, clientOrderKey: key };
  }
  
  // ========== Parse Events ==========
  export function parseDepositCreatedFromReceipt(receipt) {
    if (!receipt || !Array.isArray(receipt.logs)) return null;
  
    const vaultAddr = VAULT.toLowerCase();
    const hits = receipt.logs.filter((lg) => {
      const addrOk = (lg.address || "").toLowerCase() === vaultAddr;
      const t0 = (lg.topics && lg.topics[0]) ? String(lg.topics[0]).toLowerCase() : "";
      return addrOk && t0 === TOPIC.DepositCreated.toLowerCase();
    });
  
    if (!hits.length) return null;
  
    // 取第一条 DepositCreated
    const lg = hits[0];
    const orderId = BigInt(lg.topics[1]); // indexed uint256
    const user = topicToAddress(lg.topics[2]); // indexed address
    const planId = BigInt(lg.topics[3]); // indexed uint256
  
    // data: amount(uint256), clientOrderKey(bytes32)
    const amount = readU256FromData(lg.data, 0);
    const clientOrderKey = readBytes32FromData(lg.data, 1);
  
    return {
      orderId: orderId.toString(),
      user,
      planId: planId.toString(),
      amountWei: amount.toString(),
      amount: formatUnits(amount, USDT_DECIMALS, 2),
      clientOrderKey,
    };
  }
  
  export default {
    VAULT,
    USDT,
    getUsdtBalanceFormatted,
    getUsdtAllowance,
    approveUsdt,
    depositToVault,
    placeOrder,
    randomClientOrderKey,
    parseDepositCreatedFromReceipt,
  };
  